import TableEmptyState from '@/components/Custom/TableEmptyState';
import TableNext from '@/components/Custom/TableNext';
import type {
  TableNextColumn,
  TableNextProps,
  TableNextRowData,
  TableNextSortColumn,
} from '@/components/Custom/TableNext.types';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import { graphql, type ResultOf } from '@/gql/graphql';
import { StatRankingFragment } from './EventStats.types';
export const GetEventStatsTable = graphql(/* GraphQL */ `
  query GetEventStatsTable(
    $eventId: uuid!
    $groupBy: String!
    $rankBy: uuid
    $rankDirection: String
    $limit: Int!
    $offset: Int!
  ) {
    badge_widget_values(
      limit: $limit
      offset: $offset
      args: {
        eventId: $eventId
        groupBy: $groupBy
        rankBy: $rankBy
        rankDirection: $rankDirection
      }
      order_by: [{ rank: asc }, { title: asc }]
    ) {
      event_id
      person_id
      team_id
      title
      color
      values
      rank
    }
    event_by_pk(id: $eventId) {
      id
      stat_widget {
        id
        default_badge_widget_column_id
        default_badge_widget_column_direction
        badge_widget_columns(order_by: { order: asc_nulls_last }) {
          id
          abbreviation
          title
          sort_order
          sort_direction
        }
      }
    }
  }
`, [StatRankingFragment]);
const PAGE_LIMIT = 25;
type EventStatValue = ResultOf<
  typeof GetEventStatsTable
>['badge_widget_values'][number];
type EventStatCellValue = number | string | null;
type EventStatsTableBaseRow = {
  id: string;
  color: string | null;
  title: string | null;
};
export type EventStatsTableRowData = EventStatsTableBaseRow;
export type EventStatsTableColumn = TableNextColumn<EventStatsTableRowData>;

function normalizeEventStatCellValue(value: unknown): EventStatCellValue {
  if (
    typeof value === 'number' ||
    typeof value === 'string' ||
    value === null
  ) {
    return value;
  }
  return null;
}

function normalizeEventStatValues(
  values: EventStatValue['values'],
): Record<string, EventStatCellValue> {
  if (!values || typeof values !== 'object' || Array.isArray(values)) {
    return {};
  }

  const normalizedValues: Record<string, EventStatCellValue> = {};
  for (const [key, value] of Object.entries(values)) {
    normalizedValues[key] = normalizeEventStatCellValue(value);
  }
  return normalizedValues;
}

export default function EventStatsTable({
  data,
  fetching,
  sortColumn,
  setSortColumn,
}: {
  data: ResultOf<typeof GetEventStatsTable> | null | undefined;
  fetching: boolean;
  sortColumn: TableNextProps<EventStatsTableRowData>['sortColumn'] | null;
  setSortColumn: (sortColumn: TableNextSortColumn) => void;
}) {
  const currentColumn =
    sortColumn ||
    (data?.event_by_pk?.stat_widget?.default_badge_widget_column_id
      ? {
          id: data.event_by_pk.stat_widget.default_badge_widget_column_id,
          direction:
            data?.event_by_pk?.stat_widget
              ?.default_badge_widget_column_direction || 'desc',
        }
      : null);
  const widgetColumns =
    data?.event_by_pk?.stat_widget?.badge_widget_columns || [];
  const badgeWidgetValues = data?.badge_widget_values || [];
  const rows: TableNextRowData<EventStatsTableRowData>[] =
    badgeWidgetValues?.map((row) => ({
      id: row.person_id || row.team_id || row.title || row.event_id || '',
      title: row.title,
      color: row.color,
      ...normalizeEventStatValues(row.values),
    })) || [];
  const trimmedRows =
    rows.length > PAGE_LIMIT ? [...rows.slice(0, rows.length - 1)] : rows;
  const renderTitleCell = (rowData: EventStatsTableRowData) => {
    return (
      <Box direction="horizontal" align="center" space="sm">
        <Text numberOfLines={1}>{rowData.title}</Text>
        <Box width={2} h="full" backgroundColor={rowData.color || ''} />
      </Box>
    );
  };
  const columns: EventStatsTableColumn[] = [
    {
      title: '',
      id: 'title',
      pin: 'left',
      minWidth: 160,
      initialSortDirection: 'asc',
      headerCellStyle: {
        justifyContent: 'flex-end',
      },
      cellStyle: {
        alignItems: 'flex-end',
      },
      renderCell: renderTitleCell,
    },
    ...widgetColumns.map((widgetColumn) => {
      const titleLength = widgetColumn?.abbreviation?.length || 0;
      return {
        title: widgetColumn?.abbreviation || '',
        tooltip: widgetColumn.title,
        id: widgetColumn.id,
        width: Math.max(64, 20 * titleLength),
        initialSortDirection: widgetColumn.sort_direction,
        onPressHeader: (column: EventStatsTableColumn) => {
          if (currentColumn?.id && currentColumn.id === column.id) {
            setSortColumn({
              id: currentColumn.id,
              direction: currentColumn?.direction === 'asc' ? 'desc' : 'asc',
            });
            return;
          }
          setSortColumn({
            id: column.id,
            direction: column.initialSortDirection || 'desc',
          });
        },
      };
    }),
  ];
  return trimmedRows.length || widgetColumns.length ? (
    <TableNext
      columns={columns}
      sortColumn={currentColumn}
      rows={trimmedRows}
      emptyContent={
        trimmedRows.length ? null : (
          <TableEmptyState
            message={fetching ? 'Loading stats…' : 'No stats to show'}
          />
        )
      }
      highlight
    />
  ) : !fetching ? (
    <TableEmptyState message="No stats to show" />
  ) : null;
}
